import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

import { DatePickers } from 'components';
import { LeaderboardConfigInput } from 'api';

enum Durations {
  Ongoing = 'ongoing',
  Start = 'start',
  End = 'end',
  Expire = 'expire',
}

const Duration: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<LeaderboardConfigInput>();
  const { start_at, end_at } = values;

  const [durations, setDurations] = useState(getDurations());

  function getDurations() {
    if (start_at && end_at) {
      return Durations.Expire;
    }

    if (start_at) {
      return Durations.Start;
    }

    if (end_at) {
      return Durations.End;
    }

    return Durations.Ongoing;
  }

  function onChange(event: ChangeEvent<HTMLSelectElement>) {
    const { target } = event;

    switch (target.value) {
      case Durations.Ongoing:
        setFieldValue('start_at', 0);
        setFieldValue('end_at', 0);
        setDurations(Durations.Ongoing);
        return;
      case Durations.Start:
        setFieldValue('end_at', 0);
        setDurations(Durations.Start);
        return;
      case Durations.End:
        setFieldValue('start_at', 0);
        setDurations(Durations.End);
        return;
      case Durations.Expire:
        setDurations(Durations.Expire);
        return;
    }
  }

  function onChangeStartDate(timestamp: number) {
    setFieldValue('start_at', timestamp);
  }

  function onChangeEndDate(timestamp: number) {
    setFieldValue('end_at', timestamp);
  }

  function renderStartDropdown() {
    if (durations === Durations.Ongoing || durations === Durations.End) {
      return null;
    }

    return (
      <Grid item xs={4}>
        <DatePickers.Select
          fullWidth
          label={t('admin:startDate')}
          value={start_at}
          setValue={onChangeStartDate}
          defaultValue={t('posting:selectTime')}
        />
      </Grid>
    );
  }

  function renderEndDropdown() {
    if (durations === Durations.Ongoing || durations === Durations.Start) {
      return null;
    }

    return (
      <Grid item xs={4}>
        <DatePickers.Select
          fullWidth
          label={t('admin:endDate')}
          value={end_at}
          setValue={onChangeEndDate}
          defaultValue={t('posting:selectTime')}
        />
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={3}>
        <Typography variant="subtitle2">{t('common:duration')}</Typography>
      </Grid>

      <Grid item xs={8} container spacing={3}>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>{t('leaderboard:duration_label')}</InputLabel>
            <Select fullWidth value={durations} onChange={onChange}>
              <MenuItem value={Durations.Ongoing}>{t('common:ongoing')}</MenuItem>
              <MenuItem value={Durations.Start}>{t('admin:startDate')}</MenuItem>
              <MenuItem value={Durations.End}>{t('admin:endDate')}</MenuItem>
              <MenuItem value={Durations.Expire}>{t('admin:startAndEndDate')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Fragment>{renderStartDropdown()}</Fragment>

        <Fragment>{renderEndDropdown()}</Fragment>
      </Grid>
    </Grid>
  );
};

export default Duration;
